import axios from 'axios';
import { apiUrl } from '../../apiBase';

// WHAT THE READER LOOKED LIKE, KEPT — the sandbox's saves, from this side.
//
// The sandbox is where design ideas are tried, and the question it exists to answer is
// "is this better than what was there before". Git answers what the CODE was; nothing
// answered what the PAGE was, and by the time the comparison is wanted the previous
// version has been saved over. So a save is a picture per phase of the Notices tab, in
// the order the phase bar lists them, each the full height of the phase rather than of
// the window — the whole reader, top to bottom, as it stood at one commit.
//
// IT IS DRAWN IN THE BROWSER AND NOT ON THE SERVER. A screenshot taken server-side means
// a headless browser, a session for it to sign in with, and a second rendering of the app
// that is only ever as true as its installation is current. The page is already rendered
// in front of somebody; the only honest picture of it is that one.
//
// SEE ALSO backend/blueprints/notice_saves.py, which holds the other half of the argument:
// why this ships in prod's repo (so the reset cannot take it) and why the pictures live
// under backend/data (so the reset cannot take those either).

export type SaveScreen = {
  idx: number;
  // The phase's tab id, and the label the phase bar had for it — stored rather than
  // resolved later, because a save is of a version and the labels move between versions.
  phase: string;
  label: string;
  width: number;
  height: number;
};

export type SaveRow = {
  id: string;
  label: string;
  docId: string;
  docName: string;
  // The commit the sandbox checkout was standing on, and its subject line. What makes the
  // list a version history rather than a pile of pictures.
  commit: string;
  subject: string;
  // How many screens the walk SAID it would take, against how many arrived. A walk that
  // died halfway reads as six of eight rather than as a complete save missing two phases.
  expected: number;
  shots: number;
  createdAt: string | null;
};

export type SaveDetail = SaveRow & { screens: SaveScreen[] };

export const listSaves = async (): Promise<SaveRow[]> =>
  (await axios.get<{ saves: SaveRow[] }>('/api/notice/saves')).data.saves ?? [];

export const readSave = async (id: string): Promise<SaveDetail> =>
  (await axios.get<SaveDetail>(`/api/notice/saves/${id}`)).data;

export const renameSave = (id: string, label: string) =>
  axios.patch(`/api/notice/saves/${id}`, { label });

export const dropSave = (id: string) => axios.delete(`/api/notice/saves/${id}`);

// A picture the browser fetches itself, so it carries the base by hand — the one rule
// every non-axios URL in this app follows (see apiBase).
export const shotUrl = (id: string, idx: number) =>
  apiUrl(`/api/notice/saves/${id}/shot/${idx}.png`);

export const openSave = async (save: {
  label: string; docId: string; docName: string; expected: number;
}): Promise<string> =>
  (await axios.post<{ id: string }>('/api/notice/saves', save)).data.id;

export const putShot = (
  id: string,
  idx: number,
  png: Blob,
  about: { phase: string; label: string; w: number; h: number },
) => axios.put(`/api/notice/saves/${id}/shot/${idx}`, png, {
  headers: { 'Content-Type': 'image/png' },
  params: about,
});

// WHICH MANUAL THE SAVES PAGE WOULD PHOTOGRAPH — the one this browser last had open.
//
// The same key the viewer keeps it under, read rather than copied: "which manual THIS
// browser had open" is one fact and it already has a home (see NoticeViewer). A save is of
// a manual's phases, so with no manual open there is nothing to take a picture of, and the
// page says so rather than offering a button that would photograph an empty reader.
export const LAST_DOC_KEY = 'notice.lastDocId';

export const lastOpenDoc = (): string => {
  try {
    return localStorage.getItem(LAST_DOC_KEY) || '';
  } catch {
    return '';
  }
};

// WHAT THE SAVE ABOUT TO BE TAKEN IS CALLED — handed to the walk that happens on the
// viewer's page.
//
// A HANDOVER AND NOT A STORE. The walk runs where the pixels are — the reader, with every
// phase mounted — and it is started from somewhere else, so the name has to cross one
// navigation. sessionStorage, taken and cleared in one read: it survives the route change
// and cannot survive anything else, which is exactly its lifetime.
//
// A PLAIN STRING, AND IT HAS TO STAY ONE. Two things write this key: the Saves page, which
// ships with the code reading it, and the headless browser that photographs the sandbox
// before a reset (scripts/capture/save.mjs) — which is ALWAYS NEWER than the page it is
// driving, because the page it drives is the version about to be replaced. Anything
// structured here is read as a name by every version that predates it, and that is not a
// hypothetical: three saves came out called `{"note":"Promotion save"}`.
//
// So the wire format is the oldest one there is, and whoever asks for a walk writes the
// whole name. An empty one is named by the server (see notice_saves._auto_label).
const WALK_KEY = 'notice.saveWalk';

export const askForSave = (label: string) => {
  try {
    sessionStorage.setItem(WALK_KEY, label);
  } catch {
    // A browser with storage off still gets the save; the server names it.
  }
};

export const takeSaveLabel = (): string => {
  try {
    const label = sessionStorage.getItem(WALK_KEY) || '';
    sessionStorage.removeItem(WALK_KEY);
    return label;
  } catch {
    return '';
  }
};

// THE PICTURE ITSELF.
//
// modern-screenshot, and the technique is the one every DOM rasteriser uses: clone the
// node, carry each element's computed style onto the clone, hand the result to the browser
// as an SVG <foreignObject> and let IT lay the page out. Which is what makes a save true —
// Tailwind v4's oklch colours, its grids, dark mode, all rendered by the same engine that
// renders the page, where a library that parses CSS itself has to reimplement all three.
//
// WHAT CHANGED, AND WHY IT WAS WORTH CHANGING. This was html-to-image, which copies about
// three hundred properties onto every cloned element whether they were set or not. A capture
// here has several thousand elements and the timings said so: three phases of quite
// different content all landed on 35 seconds, which is a fixed cost that has nothing to do
// with what is being photographed. This one diffs each element against the browser's default
// style for its tag and copies only what differs — which on a Tailwind page is a handful of
// properties per node instead of three hundred.
//
// The next lever, if that floor is still there: `includeStyleProperties`, which names the
// properties outright. Faster again and a real risk of a save that quietly looks wrong, so
// it is not taken until the measurement says the walk is still where the time goes.
//
// WHAT IT CANNOT DRAW, said here rather than found later: a WebGL canvas is read with
// toDataURL, and a renderer whose drawing buffer was not preserved hands back an empty one.
// The two live scenes in this app are built with `preserveDrawingBuffer` on the sandbox for
// exactly this (see meshScene and assemblyScene) — it costs a copy per frame, and it is the
// difference between Phase 5 being in the save and being a grey rectangle.
//
// IMPORTED WHERE IT IS USED, and that is the point of the `await import`. This is the one
// feature in the app that needs a DOM rasteriser, it exists on one of the three builds, and
// it is reached by pressing a button — so a static import would put the library in prod's
// bundle for every reader who will never take a save. Vite splits it into its own chunk and
// the browser fetches it the first time somebody does.
//
// AND THE WEB FONTS ARE NOT EMBEDDED (`font: false`). The brand faces are served by
// Fontshare, and embedding a font means READING the @font-face rule that names it — which
// the browser refuses on a cross-origin stylesheet, then refuses again when the library
// tries to fetch that stylesheet itself. The first walk sat there for minutes before its
// first picture. There is nothing to embed, so it does not go looking: a save comes out in
// the fallback stack and every other pixel — layout, spacing, colour, dark mode — is exact.
// The cure, the day the letterforms matter: serve the two faces from this origin.
// AT THE PAGE'S OWN SIZE. A smaller picture was tried and abandoned with the thing it was
// for: `scale` (and html-to-image's `pixelRatio`) size the CANVAS and not the drawing, so
// they save the encode and not one pixel of the rasterising — and shrinking the drawing
// itself, with width/height and a transform on the clone, has to carry an explicit px size
// or the full-width shell simply lays out narrower and the picture is of a window nobody
// has. Worth writing down: it is the trap anybody optimising this will fall into next.
export async function shootElement(node: HTMLElement): Promise<Blob> {
  const { domToBlob } = await import('modern-screenshot');
  const blob = await domToBlob(node, {
    scale: 1,
    // THE PAGE'S OWN BACKGROUND, and it has to be said. The captured shell has none of its
    // own — the colour comes from the body — so without this a dark-mode save is
    // white-on-transparent, which is white-on-white in every image viewer there is.
    backgroundColor: getComputedStyle(document.body).backgroundColor,
    font: false,
    // A picture that never loads must not hold up a save. Half the page's own budget for a
    // screen, so an asset that is not coming is given up on well before the screen is.
    timeout: 15_000,
    // THE SEVEN PHASES THAT ARE NOT ON SCREEN, LEFT OUT.
    //
    // The reader keeps every phase MOUNTED and hides all but one (see NoticeViewer, which
    // argues why). A rasteriser does not care that a subtree is `display: none` — it clones
    // it and reads the style of every node in it — so each shot was drawing the whole
    // pipeline to photograph an eighth of it, eight times over.
    //
    // MARKED, AND NOTHING ELSE. The hidden phases carry `data-capture-skip`, written by the
    // same expression that hides them, so the two cannot drift: what is not on screen is
    // not in the picture.
    //
    // NOT `hidden` OFF THE CLASS LIST, which was here for one commit and took the sidebar
    // out of every save. Tailwind hides responsively by naming BOTH states — the desktop
    // rail is `hidden h-dvh md:flex`, hidden on a phone and shown on everything else — so a
    // string test for that class drops elements that are on screen. It cost the rail, and
    // then a grey band down the right of every picture: the content column's computed width
    // is carried onto the clone, so with 224px of sidebar gone from the left, 224px of
    // shell background was left over on the right.
    //
    // The lesson is the one this file keeps learning: a class name says what a page ASKED
    // for at some width, and only the computed style says what is true — which is the
    // expensive thing to ask, so the marks are put there by the code doing the hiding.
    filter: (el) => !(el instanceof HTMLElement) || !el.hasAttribute('data-capture-skip'),
  });
  if (!blob) throw new Error('empty picture');
  return blob;
}

// A STAGE THAT MUST NOT BE ABLE TO HANG THE WALK.
//
// Everything here is somebody else's code doing network I/O — a fetch with no timeout, a
// decode that never settles — and a walk that stops has no way to say so: the pill reads
// "screen 1 of 8" for as long as anybody is willing to watch it. A stage that overruns is
// reported as the stage it was, by name, which is a fact somebody can act on.
export async function inTime<T>(what: string, ms: number, work: Promise<T>): Promise<T> {
  let timer = 0;
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, fail) => {
        timer = window.setTimeout(() => fail(new Error(`${what} took longer than ${ms / 1000}s`)), ms);
      }),
    ]);
  } finally {
    window.clearTimeout(timer);
  }
}
